import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdaterChainId } from '~/hooks'
import Skeleton from '~/components/Skeleton'
import Tooltip from '~/components/Tooltip'
import { StyledDetailWrapper } from '~/components/Exchange/styled'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useSpotSwapUserConfig } from '~/state/dex/hooks'
import { RateState } from '../../types'
import { PercentRoute } from '@tokenlon/v6-sdk'
import { findAllUniqueProtocolsAndMergeUniswap } from './utils'
import { showSkeletonStateList } from '../../helpers'
import { isMainnetOrSepolia } from '~/utils/chain'
import {
  StyledProtocolsWrapper,
  StyledProtocolIcon,
  StyledProtocolTextIcon,
} from './styled'
import IconCurve from '~/assets/images/protocol-logo-curve-with-bg.svg'
import IconSushiSwap from '~/assets/images/protocol-logo-sushi-swap-with-bg.svg'
import IconUniswap from '~/assets/images/protocol-logo-uniswap-with-bg.svg'
import IconBalancer from '~/assets/images/protocol-logo-balancer-with-bg.svg'
import IconPancake from '~/assets/images/protocol-logo-pancake-with-bg.svg'
import IconDsspsm from '~/assets/images/protocol-logo-dsspsm-with-bg.svg'
import IconSynapse from '~/assets/images/protocol-logo-synapse-with-bg.svg'
import IconSynthetix from '~/assets/images/protocol-logo-synthetix-with-bg.svg'
import IconDodo from '~/assets/images/protocol-logo-dodo-with-bg.svg'
import IconMaverick from '~/assets/images/protocol-logo-maverick-with-bg.svg'
import IconTokenlon from '~/assets/images/protocol-logo-tokenlon-with-bg.svg'
import IconBebop from '~/assets/images/protocol-logo-bebop-with-bg.svg'
import IconACross from '~/assets/images/protocol-logo-across.svg'

interface Props {
  rateState: RateState
  routes: PercentRoute[]
}

export const protocolToIconMap = {
  'uniswap v2': IconUniswap,
  'uniswap v3': IconUniswap,
  uniswap: IconUniswap,
  sushiswap: IconSushiSwap,
  curve: IconCurve,
  balancer: IconBalancer,
  'pancake v3': IconPancake,
  dsspsm: IconDsspsm,
  synapse: IconSynapse,
  synthetix: IconSynthetix,
  dodo: IconDodo,
  maverick: IconMaverick,
  tokenlon: IconTokenlon,
  bebop: IconBebop,
}

const CrossSwapDetail: React.FC = () => {
  const { t } = useTranslation()

  return (
    <Tooltip
      title={t('smart_routing_cross_swap_tip')}
      trigger="hover"
      placement="left"
      mobilePlacement="left"
    >
      <StyledProtocolIcon src={IconACross} />
    </Tooltip>
  )
}

const SmartRoutingDetail: React.FC<{ routes: PercentRoute[] }> = ({
  routes,
}) => {
  const toggle = useModalToggle(ApplicationModal.SMART_ROUTING_DETAIL)
  const protocols = useMemo(
    () =>
      routes ? findAllUniqueProtocolsAndMergeUniswap(routes) : ['tokenlon'],
    [routes],
  )

  return (
    <StyledProtocolsWrapper
      justify="flex-end"
      align="center"
      gap={4}
      onClick={toggle}
    >
      {protocols.map((protocol, index) => {
        const protocolIcon = protocolToIconMap[protocol]
        return protocolIcon ? (
          <StyledProtocolIcon key={index} src={protocolIcon} />
        ) : (
          <StyledProtocolTextIcon key={index}>
            {protocol.substring(0, 2).toUpperCase()}
          </StyledProtocolTextIcon>
        )
      })}
    </StyledProtocolsWrapper>
  )
}

const SmartRoutingVisualizer: React.FC<Props> = ({ rateState, routes }) => {
  const { t } = useTranslation()
  const chainId = useUpdaterChainId()
  const isMainnetOrSepoliaFlag = isMainnetOrSepolia(chainId)
  const { isCrossSwap } = useSpotSwapUserConfig()
  const showSkeleton = showSkeletonStateList.includes(rateState)

  // 跨链交易会展示；普通交易只有主网和 sepolia 会展示，其他网络目前都是接第三方 API 没有拆单
  const display = isCrossSwap || isMainnetOrSepoliaFlag

  if (!display) {
    return null
  }

  return (
    <StyledDetailWrapper>
      <span>{t('smart_routing')}</span>

      {showSkeleton ? (
        <Skeleton />
      ) : isCrossSwap ? (
        <CrossSwapDetail />
      ) : (
        <SmartRoutingDetail routes={routes} />
      )}
    </StyledDetailWrapper>
  )
}

export default SmartRoutingVisualizer
